/* eslint-disable react/display-name */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Tag,
  TagCloseButton,
  TagLabel,
  useToast,
  Skeleton,
  Link as ChakraLink,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  GetLimitedPartnerMappingsForAliasingQueryGQLType,
  useUpdateLimitedPartnerMappingMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { AddLimitedPartnerButton } from './AddLimitedPartnerButton';
import { LimitedPartnerSearch } from './LimitedPartnerSearch';
import { MatchStatus } from './types';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import {
  successToastConfig,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

type HandleUpdateMappingProps = {
  mappingId: string;
  lpId: string | null;
};

const LimitedPartnerCell = ({
  limitedPartnerName,
  limitedPartnerId,
  limitedPartnerAlias,
  limitedPartnerMappingId,
  lenderOrganizationId,
  handleUpdateMapping,
  suggestedLimitedPartnerId,
  suggestedLimitedPartnerName,
}: {
  limitedPartnerName: string | null;
  limitedPartnerId: string | null;
  limitedPartnerAlias: string;
  limitedPartnerMappingId: string;
  lenderOrganizationId: string;
  handleUpdateMapping: ({ mappingId, lpId }: HandleUpdateMappingProps) => void;
  suggestedLimitedPartnerId: string | null;
  suggestedLimitedPartnerName: string | null;
}) => {
  const [limitedPartnerIdForMapping, setLimitedPartnerIdForMapping] = useState(
    limitedPartnerId,
  );
  const [
    limitedPartnerNameForMapping,
    setLimitedPartnerNameForMapping,
  ] = useState(limitedPartnerName);
  const [showManualSearch, setShowManualSearch] = useState(false);

  useEffect(() => {
    setLimitedPartnerIdForMapping(limitedPartnerId);
    setLimitedPartnerNameForMapping(limitedPartnerName);
  }, [limitedPartnerName, limitedPartnerId]);

  return (
    <>
      {limitedPartnerNameForMapping && limitedPartnerIdForMapping && (
        <Tag variant="solid-muted" colorScheme="green" size="lg">
          <Link
            to={`/limited-partners/details/${limitedPartnerIdForMapping}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.blue[400],
              fontSize: '1rem',
              textDecoration: 'underline',
              wordBreak: 'break-all',
            }}
          >
            <TagLabel>{limitedPartnerNameForMapping}</TagLabel>
          </Link>

          <TagCloseButton
            onClick={() => {
              finleyAssert(
                limitedPartnerName,
                'Limited partner name should exist for mapping',
              );
              setLimitedPartnerIdForMapping(null);
              setLimitedPartnerNameForMapping(null);
              handleUpdateMapping({
                lpId: null,
                mappingId: limitedPartnerMappingId,
              });
            }}
          />
        </Tag>
      )}
      {limitedPartnerIdForMapping === null && (
        <Flex direction="column" gap="8px" alignItems="baseline">
          {suggestedLimitedPartnerId && suggestedLimitedPartnerName ? (
            <>
              {!showManualSearch ? (
                <>
                  <Flex alignItems="center" gap="8px">
                    <Tag
                      variant="solid-muted"
                      colorScheme="blue"
                      size="lg"
                      minWidth="0"
                      maxWidth="calc(100% - 60px)"
                    >
                      <Link
                        to={`/limited-partners/details/${suggestedLimitedPartnerId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: colors.blue[400],
                          fontSize: '1rem',
                          textDecoration: 'underline',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TagLabel>{suggestedLimitedPartnerName}</TagLabel>
                      </Link>
                    </Tag>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      flexShrink={0}
                      onClick={() => {
                        setLimitedPartnerIdForMapping(
                          suggestedLimitedPartnerId,
                        );
                        setLimitedPartnerNameForMapping(
                          suggestedLimitedPartnerName,
                        );
                        handleUpdateMapping({
                          lpId: suggestedLimitedPartnerId,
                          mappingId: limitedPartnerMappingId,
                        });
                      }}
                    >
                      Match
                    </Button>
                  </Flex>
                  <Button
                    variant="link"
                    size="sm"
                    color="gray.500"
                    textDecoration="underline"
                    onClick={() => setShowManualSearch(true)}
                  >
                    Choose different LP
                  </Button>
                </>
              ) : (
                <>
                  <LimitedPartnerSearch
                    lenderOrganizationId={lenderOrganizationId}
                    onSelect={(lpId, lpName) => {
                      setLimitedPartnerIdForMapping(lpId);
                      setLimitedPartnerNameForMapping(lpName);
                      handleUpdateMapping({
                        lpId,
                        mappingId: limitedPartnerMappingId,
                      });
                    }}
                    defaultQuery={limitedPartnerAlias}
                  />
                  <AddLimitedPartnerButton
                    onAddLimitedPartner={(params) => {
                      handleUpdateMapping({
                        lpId: params.lpId,
                        mappingId: limitedPartnerMappingId,
                      });
                    }}
                  />
                  <Button
                    variant="link"
                    size="sm"
                    color="gray.500"
                    textDecoration="underline"
                    onClick={() => setShowManualSearch(false)}
                  >
                    Back to suggestion
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <LimitedPartnerSearch
                lenderOrganizationId={lenderOrganizationId}
                onSelect={(lpId, lpName) => {
                  setLimitedPartnerIdForMapping(lpId);
                  setLimitedPartnerNameForMapping(lpName);
                  handleUpdateMapping({
                    lpId,
                    mappingId: limitedPartnerMappingId,
                  });
                }}
                defaultQuery={limitedPartnerAlias}
              />
              <AddLimitedPartnerButton
                onAddLimitedPartner={(params) => {
                  handleUpdateMapping({
                    lpId: params.lpId,
                    mappingId: limitedPartnerMappingId,
                  });
                }}
              />
            </>
          )}
        </Flex>
      )}
    </>
  );
};

type AliasMatchRow = {
  mappingId: string;
  limitedPartnerAlias: string;
  limitedPartnerId: string | null;
  limitedPartnerName: string | null;
  committedCapital: number;
  reportingDate: Util.FullDateStr;
  matchedByUserName: string | null;
  matchedByUserEmail: string | null;
  loanProductId: string;
  creditFacilityId: string;
  suggestedLimitedPartnerId: string | null;
  suggestedLimitedPartnerName: string | null;
};

const AliasMatchingTable = ({
  rows,
  lenderOrganizationId,
  handleUpdateMapping,
}: {
  rows: AliasMatchRow[];
  lenderOrganizationId: string;
  handleUpdateMapping: ({ mappingId, lpId }: HandleUpdateMappingProps) => void;
}) => {
  const columns = useMemo(() => {
    const tableColumns: ColumnDef<AliasMatchRow, any>[] = [
      {
        id: 'limitedPartnerAlias',
        accessorKey: 'limitedPartnerAlias',
        header: 'Limited Partner Alias',
      },
      {
        id: 'reportingDate',
        accessorKey: 'reportingDate',
        header: 'Reporting Date',
        cell: (cell) =>
          commonDate.fullDateStrToHumanDateString(
            cell.row.original.reportingDate,
          ),
      },
      {
        id: 'committedCapital',
        accessorKey: 'committedCapital',
        header: 'Committed Capital',
        cell: (cell) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: Number(cell.row.original.committedCapital),
          }),
      },
      {
        id: 'updatedBy',
        header: 'Matched By',
        cell: ({ row }) => (
          <>
            {row.original.matchedByUserName && (
              <Typography variant="detail-muted">
                {row.original.matchedByUserName}
              </Typography>
            )}
            {row.original.matchedByUserEmail && (
              <ChakraLink
                fontSize="sm"
                fontWeight="bold"
                color="blue.400"
                href={`mailto:${row.original.matchedByUserEmail}`}
                textDecoration="underline"
              >
                {row.original.matchedByUserEmail}
              </ChakraLink>
            )}
          </>
        ),
      },
      {
        id: 'limitedPartner',
        accessorKey: 'limitedPartnerName',
        header: 'Limited Partner',
        cell: ({ row }) => (
          <LimitedPartnerCell
            limitedPartnerId={row.original.limitedPartnerId}
            limitedPartnerName={row.original.limitedPartnerName}
            limitedPartnerAlias={row.original.limitedPartnerAlias}
            limitedPartnerMappingId={row.original.mappingId}
            lenderOrganizationId={lenderOrganizationId}
            handleUpdateMapping={handleUpdateMapping}
            suggestedLimitedPartnerId={row.original.suggestedLimitedPartnerId}
            suggestedLimitedPartnerName={
              row.original.suggestedLimitedPartnerName
            }
          />
        ),
      },
    ];

    return tableColumns;
  }, [handleUpdateMapping, lenderOrganizationId]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.mappingId,
  });

  return <TableBETA table={table} />;
};

type AliasingMatchingTablesProps = {
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType;
  lenderOrganizationId: string;
  refetchMappingsForAliasing: () => void;
  matchStatusForTables: MatchStatus;
};
const AliasMatchingTables = ({
  aliasingData,
  lenderOrganizationId,
  refetchMappingsForAliasing,
  matchStatusForTables,
}: AliasingMatchingTablesProps) => {
  const aliasMatchingDataByFacility = useMemo(() => {
    return aliasingData.limitedPartnerMappingsByFacility.reduce(
      (acc, { creditFacilityName, mappings }) => {
        const aliasMatchRows: AliasMatchRow[] = mappings
          .filter(
            (mapping) =>
              matchStatusForTables === 'all' ||
              (matchStatusForTables === 'matched' &&
                mapping.limitedPartnerId) ||
              (matchStatusForTables === 'notMatched' &&
                !mapping.limitedPartnerId),
          )
          .map((mapping) => ({
            mappingId: mapping.mappingId,
            limitedPartnerAlias: mapping.limitedPartnerAlias,
            limitedPartnerId: mapping.limitedPartnerId ?? null,
            limitedPartnerName: mapping.limitedPartnerName ?? null,
            committedCapital: mapping.committedCapital,
            reportingDate: mapping.reportingDate,
            matchedByUserEmail: mapping.matchedByUserEmail ?? null,
            matchedByUserName: mapping.matchedByUserName ?? null,
            loanProductId: mapping.loanProductId,
            creditFacilityId: mapping.creditFacilityId,
            suggestedLimitedPartnerId:
              mapping.suggestedLimitedPartnerId ?? null,
            suggestedLimitedPartnerName:
              mapping.suggestedLimitedPartnerName ?? null,
          }));
        return {
          ...acc,
          ...(aliasMatchRows.length > 0
            ? { [creditFacilityName]: aliasMatchRows }
            : {}),
        };
      },
      {} as { [creditFacilityName: string]: AliasMatchRow[] },
    );
  }, [aliasingData, matchStatusForTables]);

  const toast = useToast();
  const bulkMappingIdsRef = useRef(new Set<string>());
  const bulkTotalRef = useRef(0);
  const bulkErrorCountRef = useRef(0);

  const [updateLimitedPartnerMapping] = useUpdateLimitedPartnerMappingMutation({
    onCompleted: (data) => {
      if (data && data.updateLimitedPartnerMapping) {
        const { limitedPartnerMappingId } = data.updateLimitedPartnerMapping;
        if (bulkMappingIdsRef.current.has(limitedPartnerMappingId)) {
          bulkMappingIdsRef.current.delete(limitedPartnerMappingId);
          if (bulkMappingIdsRef.current.size === 0) {
            const total = bulkTotalRef.current;
            const errors = bulkErrorCountRef.current;
            const succeeded = total - errors;
            if (succeeded > 0) {
              toast({
                ...successToastConfig,
                id: `auto-match-bulk-toast-${_.uniqueId()}`,
                title: `Successfully auto-matched ${succeeded} ${
                  succeeded === 1 ? 'entry' : 'entries'
                }.`,
              });
            }
            bulkTotalRef.current = 0;
            bulkErrorCountRef.current = 0;
            refetchMappingsForAliasing();
          }
          return;
        }
        const {
          limitedPartnerId,
          limitedPartnerAlias,
          limitedPartnerName,
        } = data.updateLimitedPartnerMapping;
        successToastConfig.id = `update-limited-partner-toast-${_.uniqueId(
          limitedPartnerMappingId,
        )}`;
        successToastConfig.title = `${limitedPartnerAlias} was ${
          limitedPartnerId ? 'matched with' : 'unmatched from'
        } ${limitedPartnerName}.`;
        toast(successToastConfig);
      }
    },
    onError: (err) => {
      if (bulkMappingIdsRef.current.size > 0) {
        bulkErrorCountRef.current += 1;
        const remainingIds = Array.from(bulkMappingIdsRef.current);
        bulkMappingIdsRef.current.delete(remainingIds[0]);
        if (bulkMappingIdsRef.current.size === 0) {
          const total = bulkTotalRef.current;
          const errors = bulkErrorCountRef.current;
          const succeeded = total - errors;
          if (succeeded > 0) {
            toast({
              ...successToastConfig,
              id: `auto-match-bulk-toast-${_.uniqueId()}`,
              title: `Auto-matched ${succeeded} of ${total} ${
                total === 1 ? 'entry' : 'entries'
              }. ${errors} failed.`,
            });
          } else {
            toast({
              ...errorToastConfig,
              title: `Auto-match failed for all ${total} ${
                total === 1 ? 'entry' : 'entries'
              }.`,
            });
          }
          bulkTotalRef.current = 0;
          bulkErrorCountRef.current = 0;
          refetchMappingsForAliasing();
        }
        return;
      }
      errorToastConfig.title = err.message;
      toast(errorToastConfig);
    },
  });

  const handleUpdateMapping = useCallback(
    ({ mappingId, lpId }: { mappingId: string; lpId: string | null }) => {
      updateLimitedPartnerMapping({
        variables: {
          limitedPartnerMappingId: mappingId,
          limitedPartnerId: lpId,
        },
      });
      setTimeout(refetchMappingsForAliasing, 150);
    },
    [refetchMappingsForAliasing, updateLimitedPartnerMapping],
  );

  const handleAutoMatchForFacility = useCallback(
    (mappings: AliasMatchRow[]): void => {
      const autoMatchable = mappings.filter(
        (m) => m.suggestedLimitedPartnerId && !m.limitedPartnerId,
      );
      bulkTotalRef.current = autoMatchable.length;
      bulkErrorCountRef.current = 0;
      bulkMappingIdsRef.current = new Set(
        autoMatchable.map((m) => m.mappingId),
      );
      for (const mapping of autoMatchable) {
        updateLimitedPartnerMapping({
          variables: {
            limitedPartnerMappingId: mapping.mappingId,
            limitedPartnerId: mapping.suggestedLimitedPartnerId,
          },
        });
      }
    },
    [updateLimitedPartnerMapping],
  );

  const facilityEntries = Object.entries(aliasMatchingDataByFacility);
  const allIndices = facilityEntries.map((_entry, i) => i);

  return (
    <Accordion allowMultiple defaultIndex={allIndices}>
      {facilityEntries.map(([creditFacilityName, mappings]) => {
        const autoMatchableCount = mappings.filter(
          (m) => m.suggestedLimitedPartnerId && !m.limitedPartnerId,
        ).length;

        return (
          <AccordionItem
            key={creditFacilityName}
            borderColor={colors.gray[200]}
            borderRadius="8px"
            borderWidth="1px"
            mt="16px"
          >
            <AccordionButton py="12px" px="16px">
              <Flex flex="1" alignItems="center" gap="8px">
                <Typography
                  fontWeight="semibold"
                  variant="section-subtitle"
                  trim
                >
                  {creditFacilityName}
                </Typography>
                <Box
                  background={colors.gray[100]}
                  borderRadius="4px"
                  padding="4px 8px"
                >
                  <Typography fontWeight="semibold" variant="detail">
                    {mappings.length}
                  </Typography>
                </Box>
              </Flex>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb="16px" px="16px">
              {autoMatchableCount > 0 && (
                <Alert
                  status="info"
                  variant="subtle"
                  borderRadius="6px"
                  mb="12px"
                >
                  <AlertIcon />
                  <AlertDescription flex="1">
                    {autoMatchableCount}{' '}
                    {autoMatchableCount === 1 ? 'alias is' : 'aliases are'}{' '}
                    ready to auto-match. They exactly match existing LP names.
                  </AlertDescription>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => handleAutoMatchForFacility(mappings)}
                  >
                    Match All
                  </Button>
                </Alert>
              )}
              <AliasMatchingTable
                rows={mappings}
                lenderOrganizationId={lenderOrganizationId}
                handleUpdateMapping={handleUpdateMapping}
              />
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export const AliasMatchingTablesContainer = ({
  aliasingData,
  lenderOrganizationId,
  refetchMappingsForAliasing,
  matchStatusForTables,
}: Omit<AliasingMatchingTablesProps, 'aliasingData'> & {
  aliasingData: GetLimitedPartnerMappingsForAliasingQueryGQLType | undefined;
}) => {
  return (
    <Box mt="2.5rem">
      {aliasingData ? (
        <AliasMatchingTables
          aliasingData={aliasingData}
          lenderOrganizationId={lenderOrganizationId}
          refetchMappingsForAliasing={refetchMappingsForAliasing}
          matchStatusForTables={matchStatusForTables}
        />
      ) : (
        <Skeleton height="80vh" />
      )}
    </Box>
  );
};
